/**
 * Module: tableStayVisibleModule
 *
 * Keeps the first <tr> (header row) of a table always visible within a scrollable container.
 * When scrolling, subsequent <tr> elements are set to display:none until the first <tr>
 * becomes visible again in the viewport of the container identified by the selector in
 * data-table-header-stay-visible-in-selector.
 *
 * Rules:
 * - The last <tr> is never hidden.
 * - If a table cell has focus, that row, the previous row, and all following rows must not be hidden.
 */

interface TableStayVisibleInstance {
    tableElement: HTMLTableElement;
    scrollContainer: HTMLElement;
    scrollHandler: () => void;
    focusInHandler: () => void;
    focusOutHandler: () => void;
    mutationObserver: MutationObserver;
    wrapperMutationObserver?: MutationObserver;
}

const instances = new Map<string, TableStayVisibleInstance>();

function update(instance: TableStayVisibleInstance): void {
    const { tableElement, scrollContainer } = instance;

    const rows = Array.from(tableElement.querySelectorAll('tr'));
    if (rows.length <= 1) return;

    const headerRow = rows[0];
    const dataRows = rows.slice(1);

    if (dataRows.length === 0) return;

    // Find the focused row index within dataRows
    const activeElement = tableElement.ownerDocument.activeElement;
    let focusedRowIndex = -1;
    if (activeElement) {
        const focusedRow = activeElement.closest('tr');
        if (focusedRow) {
            focusedRowIndex = dataRows.indexOf(focusedRow as HTMLTableRowElement);
        }
    }

    // Get container viewport bounds and calculate the visible area offset
    const containerRect = scrollContainer.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(scrollContainer);
    
    // Calculate the offset from padding and border
    const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
    const borderTop = parseFloat(containerStyle.borderTopWidth) || 0;
    const topOffset = paddingTop + borderTop;
    
    // Adjust the effective top boundary of the visible area
    const effectiveTop = containerRect.top + topOffset;
    
    const headerRect = headerRow.getBoundingClientRect();
    
    // Check if header row is fully visible in the container (considering offset)
    // Header is only visible if its top edge is still at or below the effective top
    // This means the header becomes invisible as soon as it starts scrolling out of view
    const headerVisible = headerRect.top >= effectiveTop;
    console.log(`Header visible: ${headerVisible}, focusedRowIndex: ${focusedRowIndex}, effectiveTop: ${effectiveTop}, headerRect.top: ${headerRect.top}, headerRect.bottom: ${headerRect.bottom}`);

    const missingHeight = effectiveTop - headerRect.top;

    // Find the table container to adjust margin
    const tableContainer = tableElement.closest('.table') as HTMLElement | null;

    if (headerVisible) {
        // Header is visible — show all rows and reset margin
        for (const row of dataRows) {
            (row as HTMLElement).style.display = '';
        }
        
        // Reset the margin when header is visible
        if (tableContainer) {
            tableContainer.style.marginTop = '';
        }
        return;
    }

    let gainedHeight = 0;

    // Header is not visible — hide rows that are above the viewport
    for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i] as HTMLElement;

        // Never hide the last row
        if (i === dataRows.length - 1) {
            row.style.display = '';
            continue;
        }

        // If a row has focus: don't hide it, the previous row, or any following rows
        if (focusedRowIndex >= 0 && i >= focusedRowIndex - 1) {
            row.style.display = '';
            continue;
        }

        // Hide rows that are above the container viewport (considering offset)
        const rowRect = row.getBoundingClientRect();
        row.style.display = 'none';
        gainedHeight += rowRect.height;
        if (gainedHeight >= missingHeight) {
            // We've hidden enough rows to make space for the header, stop hiding more rows
            break;
        }
    }

    // Apply the margin offset to compensate for hidden rows
    if (tableContainer) {
        tableContainer.style.marginTop = `${gainedHeight}px`; 
    }
}

function setupTable(tableElement: HTMLTableElement, tableId: string): void {
    const selector = tableElement.getAttribute('data-table-header-stay-visible-in-selector');
    if (!selector) return;

    console.log(`Initializing tableStayVisible for tableId: ${tableId} with selector: ${selector}`);


    const scrollContainer = tableElement.closest(selector) as HTMLElement | null ?? document.querySelector(selector) as HTMLElement | null;
    if (!scrollContainer) return;

    console.log(`Found scroll container for tableId: ${tableId}`);

    // Check if this table is already initialized
    const existingInstance = instances.get(tableId);
    if (existingInstance && existingInstance.tableElement === tableElement) {
        return;
    }

    // Clean up existing instance for this tableId
    if (existingInstance) {
        disposeInstance(existingInstance);
    }

    const instance: TableStayVisibleInstance = {
        tableElement,
        scrollContainer,
        scrollHandler: () => update(instance),
        focusInHandler: () => update(instance),
        focusOutHandler: () => update(instance),
        mutationObserver: new MutationObserver(() => {
            requestAnimationFrame(() => update(instance));
        })
    };

    instances.set(tableId, instance);

    scrollContainer.addEventListener('scroll', instance.scrollHandler, { passive: true });
    tableElement.addEventListener('focusin', instance.focusInHandler);
    tableElement.addEventListener('focusout', instance.focusOutHandler);

    // Observe row changes in the table
    instance.mutationObserver.observe(tableElement, { childList: true, subtree: true });

    // Initial update
    update(instance);
}

function initialize(tableId: string): void {
    const wrapper = document.getElementById(tableId);
    if (!wrapper) {
        console.warn(`TableStayVisibleModule: Wrapper with id "${tableId}" not found`);
        return;
    }

    console.log(`Initializing tableStayVisible for wrapper: ${tableId}`);

    // Clean up existing instance
    dispose(tableId);

    // Find existing table
    const tableElement = wrapper.querySelector('table[data-table-header-stay-visible-in-selector]') as HTMLTableElement | null;
    if (tableElement) {
        setupTable(tableElement, tableId);
    }

    // Set up MutationObserver to watch for new tables being added to this wrapper
    const wrapperMutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;
                        
                        // Check if the added node itself is a table with the attribute
                        if (element.tagName === 'TABLE' && element.hasAttribute('data-table-header-stay-visible-in-selector')) {
                            console.log(`New table added to wrapper: ${tableId}`);
                            setupTable(element as HTMLTableElement, tableId);
                        }
                        
                        // Check if any descendant is a table with the attribute
                        const tables = element.querySelectorAll('table[data-table-header-stay-visible-in-selector]');
                        if (tables.length > 0) {
                            console.log(`${tables.length} new table(s) found in wrapper: ${tableId}`);
                            tables.forEach((table) => {
                                setupTable(table as HTMLTableElement, tableId);
                            });
                        }
                    }
                });
            }
        }
    });

    wrapperMutationObserver.observe(wrapper, { childList: true, subtree: true });
    console.log(`Set up wrapper MutationObserver for tableId: ${tableId}`);

    // Store the wrapper observer in the instance
    const instance = instances.get(tableId);
    if (instance) {
        instance.wrapperMutationObserver = wrapperMutationObserver;
    } else {
        // If no table exists yet, create a temporary instance just for the wrapper observer
        const tempInstance: TableStayVisibleInstance = {
            tableElement: null as any,
            scrollContainer: null as any,
            scrollHandler: () => {},
            focusInHandler: () => {},
            focusOutHandler: () => {},
            mutationObserver: null as any,
            wrapperMutationObserver
        };
        instances.set(tableId, tempInstance);
    }
}

function disposeInstance(instance: TableStayVisibleInstance): void {
    if (instance.scrollContainer && instance.scrollHandler) {
        instance.scrollContainer.removeEventListener('scroll', instance.scrollHandler);
    }
    if (instance.tableElement && instance.focusInHandler) {
        instance.tableElement.removeEventListener('focusin', instance.focusInHandler);
    }
    if (instance.tableElement && instance.focusOutHandler) {
        instance.tableElement.removeEventListener('focusout', instance.focusOutHandler);
    }
    if (instance.mutationObserver) {
        instance.mutationObserver.disconnect();
    }
    if (instance.wrapperMutationObserver) {
        instance.wrapperMutationObserver.disconnect();
    }

    // Reset all row visibility
    if (instance.tableElement) {
        const rows = instance.tableElement.querySelectorAll('tr');
        rows.forEach(row => (row as HTMLElement).style.display = '');
    }
}

function dispose(tableId: string): void {
    const instance = instances.get(tableId);
    if (!instance) return;

    disposeInstance(instance);
    instances.delete(tableId);
}

function initializeAll(): void {
    console.log('TableStayVisibleModule: Initializing all tables with data-table-header-stay-visible-in-selector');
    
    // Find all existing tables with the data attribute
    const tables = document.querySelectorAll('table[data-table-header-stay-visible-in-selector]');
    
    tables.forEach((table) => {
        // Find the closest parent with an ID to use as wrapper
        let wrapper = table.closest('[id]') as HTMLElement | null;
        
        if (!wrapper) {
            // If no parent with ID exists, create a unique wrapper
            const uniqueId = `table-wrapper-${Math.random().toString(36).substr(2, 9)}`;
            console.warn(`Table found without ID wrapper, creating wrapper with ID: ${uniqueId}`);
            
            // We can't easily wrap it here, so skip this table
            // In production, tables should always be in a wrapper with ID
            return;
        }
        
        console.log(`Found table in wrapper: ${wrapper.id}`);
        initialize(wrapper.id);
    });
    
    // Set up a global MutationObserver to watch for new tables being added anywhere in the document
    const globalObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;
                        
                        // Check if the added node itself is a table with the attribute
                        if (element.tagName === 'TABLE' && element.hasAttribute('data-table-header-stay-visible-in-selector')) {
                            const wrapper = element.closest('[id]') as HTMLElement | null;
                            if (wrapper) {
                                console.log(`New table detected in wrapper: ${wrapper.id}`);
                                initialize(wrapper.id);
                            }
                        }
                        
                        // Check if any descendant is a table with the attribute
                        const tables = element.querySelectorAll('table[data-table-header-stay-visible-in-selector]');
                        tables.forEach((table) => {
                            const wrapper = table.closest('[id]') as HTMLElement | null;
                            if (wrapper) {
                                console.log(`New table detected in wrapper: ${wrapper.id}`);
                                initialize(wrapper.id);
                            }
                        });
                    }
                });
            }
        }
    });
    
    // Observe the entire document body for new tables
    globalObserver.observe(document.body, { childList: true, subtree: true });
    console.log('TableStayVisibleModule: Global MutationObserver set up');
}

const TableStayVisibleModule = {
    initialize,
    dispose,
    initializeAll
};

export type ITableStayVisibleModule = typeof TableStayVisibleModule;

export default TableStayVisibleModule;
